import { zodResolver } from '@hookform/resolvers/zod'
import { Column } from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'

import { getTripBooking } from '../../../api/trip-bookings/get-trip-booking.ts'
import { getTripBookingTripPlaceToCenterRequests } from '../../../api/trip-bookings/get-trip-booking-allocations.ts'
import { useDataProps } from '../../../lib/hooks/use-data-props.ts'
import { TransportTypeEnum } from '../../../lib/types/enums/trip/trip-type.enum.ts'
import { TripStepTypeEnum } from '../../../lib/types/enums/trip-bookings/trip-step-type.enum.ts'
import { TripBookingTypes } from '../../../lib/types/models/trip-booking/trip-booking.ts'
import { TripPlaceToCenterRequestPartial } from '../../../lib/types/models/trip-place-to-center-request/trip-place-to-center-request.ts'
import { BaseFormProps, DataProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import { ItemNotFound } from '../../elements/item-not-found'
import LoadingScreen from '../../elements/loading/loading-screen'
import ValidationButtons from '../../elements/validation-buttons'
import { AllocatedPlaceFormInput } from '../trip-dates/trip-date-booking-to-center-form'
import { tripDateBookingToCenterFormSchema } from '../trip-dates/trip-dates.forms.ts'
import { TripBookingAllocationsTable } from './trip-booking.ui.tsx'

type Props = DataProps<TripBookingTypes.Light, BaseFormProps<AllocatedPlaceFormInput>>

export function TripBookingPlacesForm(props: Props) {
  const {
    id,
    data: tripbooking,
    refetch: _refetch,
    ...validationButtonProps
  } = useDataProps<TripBookingTypes.Light, BaseFormProps<AllocatedPlaceFormInput>>(props, (id) =>
    getTripBooking(id, 'LIGHT')
  )

  const { data: tripPlaceToCenterRequests, isPending } = useQuery({
    queryKey: ['trip-booking', 'trip-place-to-center-requests', id, 'PARTIAL'],
    queryFn: () => getTripBookingTripPlaceToCenterRequests<'PARTIAL'>(id, { version: 'PARTIAL' }),
    enabled: !!id
  })

  const form = useForm<AllocatedPlaceFormInput>({
    resolver: zodResolver(tripDateBookingToCenterFormSchema(tripPlaceToCenterRequests?.data)),
    defaultValues: { data: [] },
    mode: FORM_MODE
  })

  const formData = form.watch('data')

  const columns: Column<TripPlaceToCenterRequestPartial>[] = useMemo(
    () => [
      {
        accessorKey: 'tripPlaceToCenterRequest.center.name',
        header: 'Centre',
        cell: ({ row }) => (
          <Typography variant="secondary-body">{row.original.center.name}</Typography>
        )
      },
      {
        accessorKey: 'numberOfStaffPlacesAllocated',
        header: '# Places réparties',
        centerCell: true,
        cell: ({ row }) => {
          const allocation = row.original

          return (
            <div className="flex flex-col justify-between items-center gap-5 py-2">
              <div className={'flex justify-center items-center w-full gap-8'}>
                <div className={'flex justify-end items-center w-full gap-2'}>
                  {allocation.allocatedPlaces.map((allocatedPlace) => {
                    // get the index of the trip booking in the form data
                    const index = formData.findIndex(
                      (tb) => tb.allocatedPlaceId === allocatedPlace.id
                    )

                    return (
                      <div
                        className={'flex justify-center items-center w-full gap-8'}
                        key={allocatedPlace.id}
                      >
                        <div className={'flex justify-end items-center w-full gap-2'}>
                          <FormField
                            control={form.control}
                            name={`data.${index}.numberOfStudentsPlacesAllocated`}
                            render={({ field }) => (
                              <FormItem className="w-28 whitespace-normal">
                                <FormControl>
                                  <ControlledInput
                                    {...field}
                                    labelClassName="w-fit"
                                    type={'number'}
                                    label={'# jeunes'}
                                    onChange={(value) => field.onChange(value ? +value : value)}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={form.control}
                            name={`data.${index}.numberOfStaffPlacesAllocated`}
                            render={({ field }) => (
                              <FormItem className="w-28 whitespace-normal">
                                <FormControl>
                                  <ControlledInput
                                    {...field}
                                    labelClassName="w-fit"
                                    type={'number'}
                                    label={'# encadrants'}
                                    onChange={(value) => field.onChange(value ? +value : value)}
                                  />
                                </FormControl>
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          )
        }
      }
    ],
    [form.control, formData]
  )

  useEffect(() => {
    if (!tripPlaceToCenterRequests) return

    const allocationPlaces = tripPlaceToCenterRequests.data.flatMap(
      (tripPlaceToCenterRequest) => tripPlaceToCenterRequest.allocatedPlaces
    )

    form.setValue(
      'data',
      allocationPlaces.map((a) => ({
        allocatedPlaceId: a.id,
        numberOfStudentsPlacesAllocated: a.numberOfStudentsPlacesAllocated,
        numberOfStaffPlacesAllocated: a.numberOfStaffPlacesAllocated
      }))
    )
  }, [tripPlaceToCenterRequests])

  if (isPending || !tripbooking) return <LoadingScreen />
  if (!tripPlaceToCenterRequests || tripPlaceToCenterRequests.data.length === 0)
    return <ItemNotFound title="Aucune date de voyage trouvée" />
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className="flex flex-col gap-3">
          {(tripbooking.directionality === TransportTypeEnum.ONE_WAY ||
            tripbooking.directionality === TransportTypeEnum.ROUND_TRIP) && (
            <TripBookingAllocationsTable
              directionality={TripStepTypeEnum.INBOUND}
              columns={columns}
              data={tripPlaceToCenterRequests.data}
            />
          )}

          {(tripbooking.directionality === TransportTypeEnum.RETURN ||
            tripbooking.directionality === TransportTypeEnum.ROUND_TRIP) && (
            <TripBookingAllocationsTable
              directionality={TripStepTypeEnum.OUTBOUND}
              columns={columns}
              data={tripPlaceToCenterRequests.data}
            />
          )}
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}
